import React, { useState, useEffect, useRef } from 'react';
import { CsvColumn } from '../types';

interface MappingStepProps {
  templateFields: CsvColumn[];
  availableFields: CsvColumn[];
  headers: string[];
  mapping: Record<string, number>;
  onMappingChange: (key: string, columnIndex: number) => void;
  onToggleField: (key: string, enabled: boolean) => void;
}

export const MappingStep: React.FC<MappingStepProps> = ({
  templateFields,
  availableFields,
  headers,
  mapping,
  onMappingChange,
  onToggleField,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const activeKeys = new Set(templateFields.map((f) => f.key));

  const toggleMenu = () => {
    if (!isMenuOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Simple check: if space below is tight (< 300px) AND more space above, open top
      if ((spaceBelow < 300 && spaceAbove > spaceBelow) || spaceBelow < 150) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <div className="csv-field-selector-container">
        <div className="csv-field-selector-dropdown">
          <button
            ref={btnRef}
            className="csv-btn csv-btn-secondary"
            id="csv-field-selector-btn"
            onClick={toggleMenu}
          >
            + Add/Remove Fields
          </button>
          <div
            ref={menuRef}
            className={`csv-field-selector-menu ${isMenuOpen ? '' : 'hidden'} ${menuPosition === 'top' ? 'top' : ''}`}
            id="csv-field-selector-menu"
          >
            <div className="csv-field-selector-header">
              Select Fields
            </div>
            {availableFields.map((field) => (
              <label key={field.key} className="csv-field-checkbox-item">
                <input
                  type="checkbox"
                  value={field.key}
                  checked={activeKeys.has(field.key)}
                  onChange={(e) => onToggleField(field.key, e.target.checked)}
                />
                <span>
                  {field.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="csv-mapping-container">
        <div
          className="csv-mapping-row csv-mapping-header-row"
        >
          <div className="csv-mapping-label">Template Fields</div>
          <div className="csv-mapping-select">Columns in your File</div>
        </div>
        <div id="csv-mapping-rows">
          {templateFields.map((field, idx) => (
            <div key={field.key} className="csv-mapping-row" data-field-index={idx}>
              <div className="csv-mapping-label">
                {field.label}
                {field.required ? <span className="csv-required-star">*</span> : ''}
              </div>
              <div className="csv-mapping-select">
                <select
                  className="csv-select"
                  data-key={field.key}
                  value={mapping[field.key] !== undefined ? mapping[field.key] : ''}
                  onChange={(e) =>
                    onMappingChange(
                      field.key,
                      e.target.value !== '' ? parseInt(e.target.value) : -1
                    )
                  }
                >
                  <option value="">Select a column</option>
                  {(headers || []).map((header, index) => (
                    <option key={`${field.key}-${index}`} value={index}>
                      {header}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
